import type {
    ShopifyActivityData,
  ShopifyFlowsData,
  ShopifyInfo,
  Template,
  ZohoActivityData,
  ZohoFlowsData,
  ZohoInfo,
} from '../../../types/integrations';

export const MOCK_ZOHO_INFO: ZohoInfo = {
  status: 'CONNECTED', // try 'CONNECTED' to see full UI
  account: null,
  lastEventAt: null,
  lastError: null,
  redirectUri: 'https://yourapp.com/admin/integrations/zoho?zoho=ok',
  dataCentres: ['in', 'com', 'eu'],
  settings: { businessName: '', createLeads: true, logChats: false },
  setup: {
    webhookUrl: 'https://api.yourapp.com/webhooks/zoho/def456',
    parameters: ['module', 'event', 'name', 'phone', 'email', 'company'],
  },
};

export const MOCK_ZOHO_FLOWS: ZohoFlowsData = {
  variables: {
    lead_name: 'Lead name',
    company: 'Company',
    phone: 'Phone',
    email: 'Email',
    business_name: 'Business name',
  },
  flows: [
    {
      key: 'zoho_welcome_lead',
      group: 'Lead Management',
      label: 'Welcome new lead',
      trigger: 'New lead created in Zoho',
      delayMinutes: 0,
      delayEditable: false,
      enabled: true,
      templateName: 'welcome_lead',
      templateLanguage: 'en',
      category: 'MARKETING',
      params: ['lead_name', 'business_name'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'Hi {{1}}, thanks for your interest in {{2}}! How can we help you today?',
      suggestedButtons: null,
    },
    {
      key: 'zoho_welcome_contact',
      group: 'Lead Management',
      label: 'Welcome new contact',
      trigger: 'New contact created in Zoho',
      delayMinutes: 0,
      delayEditable: false,
      enabled: true,
      templateName: 'welcome_contact',
      templateLanguage: 'en',
      category: 'MARKETING',
      params: ['lead_name', 'business_name'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'Hi {{1}}, welcome to {{2}}! We are here if you need anything.',
      suggestedButtons: null,
    },
    {
      key: 'zoho_create_lead',
      group: 'WhatsApp to Zoho',
      label: 'Create lead from WhatsApp',
      trigger: 'New WhatsApp message from unknown number',
      delayMinutes: 0,
      delayEditable: false,
      enabled: false,
      templateName: null,
      templateLanguage: 'en',
      category: 'UTILITY',
      params: [],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'This flow creates a Zoho lead automatically. No template needed.',
      suggestedButtons: null,
    },
  ],
};

export const MOCK_ZOHO_ACTIVITY: ZohoActivityData = {
  sent30d: 0,
  scheduled: 0,
  failed30d: 0,
  total: 0,
  results: [],
};

export const MOCK_TEMPLATES: Template[] = [
  { name: 'welcome_lead', language: 'en', status: 'APPROVED' },
  { name: 'welcome_contact', language: 'en', status: 'APPROVED' },
];

export const MOCK_SHOPIFY_INFO: ShopifyInfo = {
  status: 'CONNECTED', // try 'NOT_CONNECTED'
  account: 'mystore.myshopify.com',
  lastEventAt: Date.now() - 3600000,
  lastError: null,
  webhookUrl: 'https://api.yourapp.com/webhooks/shopify/abc123',
  settings: {
    storeName: 'My Store',
    storeUrl: 'https://mystore.myshopify.com',
    discountCode: 'WELCOME10',
    reviewUrl: 'https://mystore.myshopify.com/reviews',
    fallbackImage: 'https://via.placeholder.com/300',
    codConfirmTag: 'cod-confirmed',
    codCancelTag: 'cod-cancelled',
    cancelOnCodReply: true,
    markPaidOnPayment: true,
  },
};

export const MOCK_SHOPIFY_FLOWS: ShopifyFlowsData = {
  variables: {
    customer_name: 'Customer name',
    order_number: 'Order number',
    order_total: 'Order total',
    tracking_url: 'Tracking URL',
    product_name: 'Product name',
    product_image: 'Product image URL',
    cart_url: 'Cart recovery URL',
    discount_code: 'Discount code',
    store_name: 'Store name',
    review_url: 'Review URL',
  },
  flows: [
    {
      key: 'shopify_abandoned_cart_1',
      group: 'Cart Recovery',
      label: 'First cart reminder',
      trigger: 'Cart abandoned for 1 hour',
      delayMinutes: 60,
      delayEditable: true,
      enabled: true,
      templateName: 'cart_reminder_1',
      templateLanguage: 'en',
      category: 'MARKETING',
      params: ['customer_name', 'cart_url'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'Hi {{1}}, you left something in your cart! Complete your purchase here: {{2}}',
      suggestedButtons: 'Button: Complete purchase (URL)',
    },
    {
      key: 'shopify_abandoned_cart_2',
      group: 'Cart Recovery',
      label: 'Second cart reminder',
      trigger: 'Cart abandoned for 24 hours',
      delayMinutes: 1440,
      delayEditable: true,
      enabled: true,
      templateName: 'cart_reminder_2',
      templateLanguage: 'en',
      category: 'MARKETING',
      params: ['customer_name', 'cart_url'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'Hi {{1}}, still thinking it over? Your cart is waiting: {{2}}',
      suggestedButtons: null,
    },
    {
      key: 'shopify_abandoned_cart_3',
      group: 'Cart Recovery',
      label: 'Third cart reminder with discount',
      trigger: 'Cart abandoned for 48 hours',
      delayMinutes: 2880,
      delayEditable: true,
      enabled: false,
      templateName: null,
      templateLanguage: 'en',
      category: 'MARKETING',
      params: [],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText: 'Hi {{1}}, here is 10% off to complete your order: {{2}}',
      suggestedButtons: null,
    },
    {
      key: 'shopify_order_confirmed',
      group: 'Order Updates',
      label: 'Order confirmation',
      trigger: 'Order created',
      delayMinutes: 0,
      delayEditable: false,
      enabled: true,
      templateName: 'order_confirmed',
      templateLanguage: 'en',
      category: 'UTILITY',
      params: ['customer_name', 'order_number', 'order_total'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'Hi {{1}}, your order {{2}} for {{3}} is confirmed!',
      suggestedButtons: null,
    },
    {
      key: 'shopify_shipped',
      group: 'Order Updates',
      label: 'Shipping notification',
      trigger: 'Order fulfilled',
      delayMinutes: 0,
      delayEditable: false,
      enabled: true,
      templateName: 'order_shipped',
      templateLanguage: 'en',
      category: 'UTILITY',
      params: ['customer_name', 'order_number', 'tracking_url'],
      headerType: 'PRODUCT_IMAGE',
      headerUrl: '',
      buttonParam: 'tracking_url',
      suggestedText:
        'Hi {{1}}, your order {{2}} has shipped! Track it here: {{3}}',
      suggestedButtons: 'Button: Track order (URL)',
    },
    {
      key: 'shopify_delivered',
      group: 'Order Updates',
      label: 'Delivery confirmation',
      trigger: 'Order delivered',
      delayMinutes: 0,
      delayEditable: false,
      enabled: true,
      templateName: 'order_delivered',
      templateLanguage: 'en',
      category: 'UTILITY',
      params: ['customer_name', 'order_number', 'review_url'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: 'review_url',
      suggestedText:
        'Hi {{1}}, your order {{2}} has been delivered! Leave a review: {{3}}',
      suggestedButtons: 'Button: Leave review (URL)',
    },
    {
      key: 'shopify_cod_confirm',
      group: 'COD',
      label: 'COD confirmation',
      trigger: 'COD order placed',
      delayMinutes: 0,
      delayEditable: false,
      enabled: true,
      templateName: 'cod_confirm',
      templateLanguage: 'en',
      category: 'UTILITY',
      params: ['customer_name', 'order_number', 'order_total'],
      headerType: 'NONE',
      headerUrl: '',
      buttonParam: '',
      suggestedText:
        'Hi {{1}}, please confirm your COD order {{2}} for {{3}}. Reply YES to confirm or CANCEL to cancel.',
      suggestedButtons: 'Buttons: YES (Quick reply), CANCEL (Quick reply)',
    },
  ],
};

export const MOCK_SHOPIFY_ACTIVITY: ShopifyActivityData = {
  sent30d: 142,
  scheduled: 8,
  failed30d: 3,
  total: 142,
  results: [
    {
      id: '1',
      flow: 'Order confirmation',
      ref: '#1001',
      name: 'Rahul Sharma',
      phone: '919876543210',
      status: 'SENT',
      sentAt: Date.now() - 3600000,
      runAt: null,
      error: null,
    },
    {
      id: '2',
      flow: 'Shipping notification',
      ref: '#1002',
      name: 'Priya Patel',
      phone: '919876543211',
      status: 'SENT',
      sentAt: Date.now() - 7200000,
      runAt: null,
      error: null,
    },
    {
      id: '3',
      flow: 'COD confirmation',
      ref: '#1003',
      name: 'Amit Kumar',
      phone: '919876543212',
      status: 'SCHEDULED',
      sentAt: null,
      runAt: Date.now() + 1800000,
      error: null,
    },
    {
      id: '4',
      flow: 'First cart reminder',
      ref: 'cart_abc',
      name: 'Sneha Reddy',
      phone: '919876543213',
      status: 'SENT',
      sentAt: Date.now() - 10800000,
      runAt: null,
      error: null,
    },
    {
      id: '5',
      flow: 'Payment link sent',
      ref: 'pl_xyz',
      name: 'Vikram Singh',
      phone: '919876543214',
      status: 'FAILED',
      sentAt: null,
      runAt: Date.now() - 14400000,
      error: 'Template not approved',
    },
  ],
}